/** The space itself in the editor tray: what it is called, how it runs, and what is on it. */

import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import type { SpaceConnection } from "@nebulize/sdk";

import {
  Button,
  ConfirmDialog,
  CopyField,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Stack,
  StatusBadge,
  Toolbar,
} from "@/components/ui";
import { spaceAddress } from "@/lib/env";
import { useCreateDraft } from "@/features/studio/lib/draft";

import { SpaceVerbs } from "../components/SpaceVerbs";
import { Mono, Muted } from "../components/Text";
import { isLive, runStatusOf } from "../lib/runState";
import { useConsole } from "../store";
import { useLoadedSpace } from "../spaces/lib/queries";
import type { SpaceActivities } from "../spaces/lib/queries";
import type { AddedActivityRow, SpaceRow } from "../types";

import { captureAsBundle } from "./lib/capture";
import { useArrangement } from "./lib/arrangement";
import { useConnections, useDetachConnection } from "./lib/connections";
import { writeDrag } from "./lib/drop";
import { versionPending } from "./lib/readiness";
import { useWires } from "./lib/wires";
import { Section } from "./tray/Section";

/** Everything added to this space, drawn or not, each one a click from its own panel. */
function ActivityList({
  activities,
  selectedId,
  onSelect,
}: {
  readonly activities: SpaceActivities;
  readonly selectedId: string | null;
  readonly onSelect: (row: AddedActivityRow) => void;
}) {
  if (activities.rows.length === 0) return <EmptyState bare headline="Nothing added" />;
  return (
    <Stack gap={2}>
      {activities.rows.map((row) => (
        <button
          key={row.added_activity_id}
          type="button"
          draggable
          onDragStart={(event) => {
            writeDrag(event.dataTransfer, {
              from: "added",
              addedActivityId: row.added_activity_id,
            });
          }}
          className={
            selectedId === row.added_activity_id
              ? "cx-editor-pick cx-editor-pick-on"
              : "cx-editor-pick"
          }
          onClick={() => {
            onSelect(row);
          }}
        >
          <span className="cx-node-name">{row.name}</span>
          <span className="cx-editor-pick-end">
            <Muted small>{row.activity_id}</Muted>
            {isLive(runStatusOf(row.mount)) ? (
              <StatusBadge tone="success">running</StatusBadge>
            ) : (
              <StatusBadge tone="neutral">stopped</StatusBadge>
            )}
            {versionPending(row) === undefined ? null : (
              <StatusBadge tone="warning">Restart to apply</StatusBadge>
            )}
          </span>
        </button>
      ))}
    </Stack>
  );
}

/** One channel or machine attached to a space, naming itself and offering to detach. */
function connectionLabel(one: SpaceConnection): string {
  return one.kind === "channel" ? (one.channel_id ?? "") : (one.machine_id ?? "");
}

function Connections({ spaceId }: { readonly spaceId: string }) {
  const connections = useConnections(spaceId);
  const detach = useDetachConnection(spaceId);
  const [confirming, setConfirming] = useState<SpaceConnection | null>(null);

  if (connections.isPending) {
    return <EmptyState bare headline="Reading what is connected" />;
  }

  if (connections.data === undefined) {
    return <ErrorState headline="Read failed" onRetry={connections.refetch} />;
  }

  if (connections.data.length === 0) {
    return <EmptyState bare headline="Nothing connected" />;
  }

  return (
    <>
      <Stack gap={2}>
        {connections.data.map((one) => (
          <div className="cx-row" key={one.connection_id}>
            <div className="cx-row-id">
              <StatusBadge tone="neutral">{one.kind}</StatusBadge>
              <Mono>{connectionLabel(one)}</Mono>
            </div>
            <Button
              size="sm"
              variant="ghost"
              aria-label={`Detach ${connectionLabel(one)}`}
              onClick={() => {
                setConfirming(one);
              }}
            >
              Detach
            </Button>
          </div>
        ))}
      </Stack>
      <ConfirmDialog
        open={confirming !== null}
        title="Detach this connection?"
        description="This space stops reaching it."
        confirmLabel="Detach"
        loading={detach.isPending}
        onCancel={() => {
          setConfirming(null);
        }}
        onConfirm={() => {
          if (confirming === null) return;
          detach.mutate(confirming.connection_id, {
            onSuccess: () => {
              setConfirming(null);
            },
          });
        }}
      />
    </>
  );
}

export interface SpacePanelProps {
  readonly space: SpaceRow;
  readonly activities: SpaceActivities;
  readonly selectedId: string | null;
  readonly onSelect: (row: AddedActivityRow) => void;
}

export function SpacePanel({ space, activities, selectedId, onSelect }: SpacePanelProps) {
  const spaceId = space.space.space_id;
  const loaded = useLoadedSpace();
  const load = useConsole((state) => state.load);
  const unload = useConsole((state) => state.unload);
  const rename = useConsole((state) => state.rename);
  const archive = useConsole((state) => state.archive);

  const [name, setName] = useState(space.space.name ?? "");
  const [confirming, setConfirming] = useState(false);
  const navigate = useNavigate();
  const createDraft = useCreateDraft();
  const arrangement = useArrangement(spaceId);
  const wires = useWires(spaceId);

  const isLoaded = loaded.data?.space_id === spaceId;
  const running = activities.rows.filter((row) => isLive(runStatusOf(row.mount)));

  return (
    <Stack gap={5}>
      <div>
        <div className="cx-node-name">{space.space.name ?? space.space.join_code}</div>
        <Muted small>{`${String(activities.rows.length)} added`}</Muted>
      </div>

      <Toolbar>
        <Button
          size="sm"
          variant={isLoaded ? "secondary" : "primary"}
          onClick={() => {
            if (isLoaded) unload();
            else load(spaceId);
          }}
        >
          {isLoaded ? "Unload" : "Load"}
        </Button>
        <SpaceVerbs spaceId={spaceId} added={activities.rows} size="sm" />
      </Toolbar>

      <CopyField label="Hand out" value={spaceAddress(space.space.join_code)} />

      <Section title="In this space">
        <ActivityList activities={activities} selectedId={selectedId} onSelect={onSelect} />
      </Section>

      <Section title="Name and address">
        <Stack gap={4}>
          <Field label="Space name">
            <Stack direction="row" gap={2}>
              <Input
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                }}
              />
              <Button
                size="sm"
                variant="secondary"
                disabled={name === (space.space.name ?? "")}
                onClick={() => {
                  rename(spaceId, name);
                }}
              >
                Save
              </Button>
            </Stack>
          </Field>
          <Field label="Join code">
            <Input defaultValue={space.space.join_code} mono readOnly />
          </Field>
          {/** Read-only: nothing serves or claims a handle. */}
          <Field label="Public handle">
            <Input
              defaultValue={space.handle ?? ""}
              mono
              readOnly
              placeholder="not claimed, and not claimable yet"
            />
          </Field>
        </Stack>
      </Section>

      <Section title="Save this space as a bundle">
        <Stack gap={2}>
          <Button
            size="sm"
            variant="secondary"
            disabled={activities.rows.length === 0}
            onClick={() => {
              const captured = captureAsBundle({
                spaceName: space.space.name ?? space.space.join_code,
                rows: activities.rows,
                arrangement: arrangement.data,
                bindings: wires.data ?? [],
              });
              createDraft.mutate(captured.manifest, {
                onSuccess: (draft) => {
                  void navigate({
                    to: "/console/studio/$draft",
                    params: { draft: draft.slug },
                  });
                },
              });
            }}
          >
            {`Capture ${String(activities.rows.length)} activities`}
          </Button>
        </Stack>
      </Section>

      <Section title="Connections">
        <Connections spaceId={spaceId} />
      </Section>

      <Section title="Archive this space">
        <Toolbar>
          <Button
            variant="destructive"
            size="sm"
            disabled={running.length > 0}
            onClick={() => {
              setConfirming(true);
            }}
          >
            Archive
          </Button>
          {running.length > 0 ? (
            <StatusBadge tone="warning">{`${String(running.length)} running`}</StatusBadge>
          ) : null}
        </Toolbar>
      </Section>

      <ConfirmDialog
        open={confirming}
        title="Archive this space?"
        description="Paired canvases go blank."
        confirmLabel="Archive"
        onCancel={() => {
          setConfirming(false);
        }}
        onConfirm={() => {
          archive(spaceId);
          setConfirming(false);
        }}
      />
    </Stack>
  );
}
